import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SessionStorageService } from '@core/session-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationStatus } from '@shared/enums/notification-status';
import { Security, SecurityTaxResultItem } from '@shared/models/security';
import { AppNotificationService, ModalService } from '@shared/services';
import { PortfolioService } from 'src/app/modules/portfolio/services/portfolio.service';
import { SecurityReviewUpdate } from '../../abstractions/security-review-update';
import { ApprovalQuestionModalComponent } from '../../components/approval-question-modal/approval-question-modal.component';
import { AccordionSecurityReview } from '../../models/edit-tax-result-approval/accordion-security-review';
import { TaxResultApprovalService } from '../../services/tax-result-approval.service';
import { TaxResultEditionService } from '../../services/tax-result-edition.service';
import { TaxResultSessionService } from '../../services/tax-result-session.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-result-accordion-list-page',
  templateUrl: './tax-result-accordion-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxResultAccordionListPageComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly taxResultApprovalService = inject(TaxResultApprovalService);
  private readonly taxResultEdition = inject(TaxResultEditionService);
  private readonly taxResultSession = inject(TaxResultSessionService);
  private readonly translateService = inject(TranslateService);
  private readonly portfolioService = inject(PortfolioService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly modalService = inject(ModalService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly taxResultSessionService = inject(TaxResultSessionService);
  private readonly destroyRef = inject(DestroyRef);

  ids: number[] = [];
  securities = signal<AccordionSecurityReview[]>([]);
  isViewPage = signal<boolean>(false);
  private securitiesById = new Map<number, Security>();

  constructor() {
    this.isViewPage.set(this.router.url.includes('view'));

    this.taxResultSession.selectedIds
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadSecuritiesWithPendingApproval();
      });

    this.taxResultSession.updateSecurity
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securityUpdate: SecurityReviewUpdate) =>
        this.updateSecurity(securityUpdate)
      );
  }

  hasErrors = computed<boolean>(() => {
    return this.securities().some(item =>
      item.securityTaxResultItems.some(taxResultItem =>
        taxResultItem.hasErrors()
      )
    );
  });

  hasChanges = computed<boolean>(() => {
    return this.securities().some(securityReview =>
      securityReview.hasChanges()
    );
  });

  hasCommentChanges = computed<boolean>(() => {
    return this.securities().some(
      securityReview =>
        securityReview.commentHasChanges() ||
        securityReview.adminCommentHasChanges()
    );
  });

  hasUnsureValue = computed<boolean>(() => {
    return this.securities().some(security =>
      security.securityTaxResultItems.some(taxResultItemReview =>
        taxResultItemReview
          .items()
          .some(taxResultItemForm => taxResultItemForm.isUnsure)
      )
    );
  });

  hasDuplicatedValue = computed<boolean>(() => {
    return this.securities().some(item =>
      item.securityTaxResultItems.some(taxResultItem =>
        taxResultItem.hasDuplicatedValues()
      )
    );
  });

  ngOnDestroy(): void {
    if (!this.taxResultApprovalService.isEditingSecuritiesRules()) {
      this.taxResultSession.clearSecuritiesIds();
    }
  }

  ngOnInit(): void {
    this.loadSecuritiesWithPendingApproval();
  }

  loadSecurities(accordionSecurities: AccordionSecurityReview[]): void {
    this.securities.set(accordionSecurities);
    this.refreshSecuritiesIndex(accordionSecurities);
  }

  cancel(): void {
    this.router.navigate(['tax-results']);
  }

  approve(): void {
    const modal = this.modalService.open(
      ApprovalQuestionModalComponent,
      {
        appModalId: 'modal-approval-question',
      },
      {
        counter: signal(this.securities().length),
        security: signal(this.securities().at(0)?.security),
      }
    );

    modal.instance.afterClosed().subscribe((isConfirm: boolean) => {
      if (isConfirm) {
        const securities = this.securities();
        const isSingle = securities.length === 1;

        if (isSingle) {
          const security = securities.at(0)!.security;
          this.confirm(
            `${security.entity.entityType.name} - ${security.securityType.name} - ${security.investmentJurisdiction.country.name}`
          );
        } else {
          this.confirm(
            this.translateService.instant('taxResult.counterSecurities', {
              counter: securities.length,
            })
          );
        }
      }
    });
  }

  removeFromSelectionHandler(item: AccordionSecurityReview): void {
    this.ids = this.ids.filter(x => x !== item.security.securityId);
    const updatedSelection = this.securities().filter(
      ({ security }: AccordionSecurityReview) =>
        item.security.securityId !== security.securityId
    );
    this.loadSecurities(updatedSelection);
    this.taxResultSession.selectSecuritiesIds(this.ids);
    if (!this.ids.length) {
      this.cancel();
    }
  }

  deleteHandler(item: AccordionSecurityReview): void {
    this.portfolioService
      .removeAcquisition(item.security.securityId)
      .subscribe(() => {
        if (this.securities().length === 1) {
          this.router.navigate(['tax-results']);
        } else {
          this.removeFromSelectionHandler(item);
        }

        this.appNotificationService.notify(
          `notifications.deleteTitleSuccess`,
          `notifications.deleteDescriptionSuccess`,
          NotificationStatus.SUCCESS,
          {
            entityName: this.translateService.instant(
              'notifications.deleteEntityName'
            ),
          }
        );
      });
  }

  saveHandler(): void {
    const securities = this.securities()
      .filter(
        security =>
          security.hasChanges() ||
          security.securityTaxResultItems.some(item => item.hasChanges())
      )
      .map(
        (securityReview: AccordionSecurityReview) => securityReview.security
      );

    this.taxResultEdition
      .updateSecurities(securities, this.taxResultSession.cache)
      .subscribe(() => {
        this.taxResultSession.cleanCache();
        this.router.navigate(['tax-results']);
      });
  }

  private confirm(confirmMessage: string): void {
    this.taxResultApprovalService
      .approveSecurities(this.securities(), this.taxResultSession.cache)
      .subscribe(() => {
        this.taxResultSession.cleanCache();
        this.sessionStorage.set('approved-successfully', confirmMessage);
        this.router.navigate(['tax-results']);
      });
  }

  private updateSecurity(updatedSecurity: SecurityReviewUpdate): void {
    const security = this.securitiesById.get(
      updatedSecurity.metadata.securityId
    );

    if (!security) {
      return;
    }

    if (security.securityTaxResultItems.length) {
      this.updateStoredSecurityTaxResultItems(security, updatedSecurity);
      return;
    }

    this.taxResultSessionService.update(updatedSecurity.securityTaxResultItem);
  }

  private loadSecuritiesWithPendingApproval(): void {
    this.ids = this.taxResultSession.getSelectedSecuritiesIds();
    this.taxResultApprovalService
      .loadSecuritiesWithPendingApproval(this.ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((accordionSecurities: AccordionSecurityReview[]) => {
        this.taxResultApprovalService.isEditingSecuritiesRules.set(false);
        this.loadSecurities(accordionSecurities);
      });
  }

  private updateStoredSecurityTaxResultItems(
    security: Security,
    { metadata, securityTaxResultItem, field }: SecurityReviewUpdate
  ): void {
    security.securityTaxResultItems = security.securityTaxResultItems!.map(
      (taxResultItem: SecurityTaxResultItem) => {
        if (
          taxResultItem.securityTaxResultItemId ===
            metadata.securityTaxResultItemId &&
          field === 'rate'
        ) {
          taxResultItem.securityTaxResultItemRate = parseFloat(
            securityTaxResultItem.rate!.value().toString()
          );
        } else if (
          field === 'explanation' &&
          taxResultItem.taxResultItemTypeId === metadata.taxResultItemTypeId
        ) {
          taxResultItem.securityTaxResultItemDescription = securityTaxResultItem
            .explanation!.value()
            .toString();
        }
        return taxResultItem;
      }
    );
  }

  private refreshSecuritiesIndex(
    accordionSecurities: AccordionSecurityReview[]
  ): void {
    this.securitiesById.clear();
    accordionSecurities.forEach(({ security }: AccordionSecurityReview) => {
      this.securitiesById.set(security.securityId, security);
    });
  }
}
